// apps/queue-frontend/src/hooks/useSSE.ts
// SSE 連線 Hook — 自動連線、重連、降級
import { useEffect, useRef, useCallback } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5007/api';
const RECONNECT_DELAY = 3000; // 3 秒後重連

interface UseSSEOptions {
  storeId: string;
  onEvent: (event: string, data: any) => void;
  enabled?: boolean;
}

export function useSSE({ storeId, onEvent, enabled = true }: UseSSEOptions) {
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const onEventRef = useRef(onEvent);

  // 保持 callback 最新
  onEventRef.current = onEvent;

  const connect = useCallback(() => {
    if (!storeId || !enabled) return;

    // 清理舊連線
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const url = `${API_URL}/sse/${storeId}`;
    const es = new EventSource(url);
    eventSourceRef.current = es;

    // 監聽所有事件類型
    const events = [
      'init',
      'ticket:issued',
      'ticket:called',
      'ticket:updated',
      'ticket:cancelled',
      'queue:updated',
      'queue:reset',
      'queue:status',
    ];

    for (const eventName of events) {
      es.addEventListener(eventName, (e: MessageEvent) => {
        try {
          const data = JSON.parse(e.data);
          onEventRef.current(eventName, data);
        } catch {
          // 解析失敗，忽略
        }
      });
    }

    es.onerror = () => {
      es.close();
      eventSourceRef.current = null;

      // 自動重連
      reconnectTimerRef.current = setTimeout(() => {
        connect();
      }, RECONNECT_DELAY);
    };
  }, [storeId, enabled]);

  useEffect(() => {
    connect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
    };
  }, [connect]);
}
